import React, { useEffect, useState } from "react";
import { ArrowDownRight, ArrowUpRight, BellRing, Loader2, Pencil, Split } from "lucide-react";
import { api, argent } from "./api";
import { Badge, Cellule, Tableau } from "./ui";

const jourFr = (iso) => (iso ? String(iso).slice(0, 10).split("-").reverse().join(".") : "");

const FILTRES = [
  { id: "tous", label: "Tous" },
  { id: "superieur", label: "Montants supérieurs" },
  { id: "inferieur", label: "Montants inférieurs" },
  { id: "echelonnes", label: "Déjà échelonnés" },
];

/* Tous les versements qui ne couvraient pas exactement la facture, conservés pour pouvoir
   les comprendre après coup : qui, quelle facture, combien en trop ou en moins, et où en
   est la facture aujourd'hui. */
export default function EcartsPaiement({ onOuvrirFamille, onEchelonner }) {
  const [data, setData] = useState(null);
  const [filtre, setFiltre] = useState("tous");
  const [err, setErr] = useState("");
  const [relecture, setRelecture] = useState("");
  /* Versements comptés deux fois (relevé rechargé que la banque a ré-exporté autrement, ou
     paiement saisi à la main puis relu dans le relevé) : c'est de l'argent faussement
     crédité, l'école doit le voir tout de suite. */
  const [dbl, setDbl] = useState(null);
  const [occupe, setOccupe] = useState(false);

  const chargerDoublons = () => api("/encaissements/doublons").then(setDbl).catch(() => {});
  const charger = () => api("/encaissements/ecarts").then(setData).catch((e) => setErr(e.message));

  // Les relevés déjà importés AVANT le correctif du lecteur pouvaient porter le montant du
  // virement suivant (versement coupé par un saut de page). On les relit une fois, tout seul,
  // à l'ouverture de l'écran : l'école n'a rien à faire, et un message n'apparaît que si un
  // montant a réellement été rectifié. Les imports suivants sont lus correctement.
  useEffect(() => {
    api("/encaissements/recontroler", { method: "POST" })
      .then((r) => {
        if (r?.nb) {
          setRelecture(`${r.nb} versement(s) rectifié(s) d'après le relevé de la banque : `
            + r.corriges.map((c) => `${c.client || c.numero} ${c.avant} → ${c.apres}`).join(" · "));
        }
      })
      .catch(() => {})
      .finally(() => { charger(); chargerDoublons(); });
  }, []);

  if (err) return <p className="text-sm" style={{ color: "#B91C1C" }}>{err}</p>;
  if (!data) return <p className="text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Lecture des encaissements…</p>;

  const items = (data.items || []).filter((e) => (filtre === "tous" ? true
    : filtre === "echelonnes" ? (e.echelonnement_nb || 0) > 0
    : e.sens === filtre));
  const nbEchelonnes = (data.items || []).filter((e) => (e.echelonnement_nb || 0) > 0).length;

  return (
    <div className="space-y-4" data-testid="liste-ecarts">
      {/* Un virement crédité deux fois fausse le solde de la famille : on le dit en haut,
          avec le geste pour le retirer. */}
      {dbl?.nb > 0 && (
        <div className="rounded-2xl px-5 py-4" data-testid="ecarts-doublons-alerte"
          style={{ background: "#FEF2F2", border: "1px solid #FECACA" }}>
          <div className="text-sm font-semibold" style={{ color: "#B91C1C" }}>
            {dbl.nb} versement(s) comptés deux fois — {argent(dbl.montant)} crédités en trop
          </div>
          <ul className="mt-1 text-[12px] list-disc pl-5" style={{ color: "#7F1D1D" }}>
            {dbl.items.slice(0, 5).map((s) => (
              <li key={`${s.cle}-${s.genre || "releve"}`}>
                {s.client} · {argent(s.montant)} · relevé du {jourFr(s.date)}
                {s.genre === "saisie_a_la_main"
                  ? ` déjà saisi à la main le ${jourFr(s.garde?.saisi_le)}`
                  : ` déjà enregistré le ${jourFr(s.garde?.enregistre_le)}`}
              </li>
            ))}
          </ul>
          <button className="btn-primary mt-2" data-testid="ecarts-doublons-corriger"
            disabled={occupe}
            onClick={async () => {
              setOccupe(true);
              try {
                await api("/encaissements/doublons/corriger", { method: "POST" });
                await charger();
                await chargerDoublons();
              } finally { setOccupe(false); }
            }}>
            {occupe ? <Loader2 size={14} className="animate-spin" /> : null}
            Retirer ces doublons et corriger les montants reçus
          </button>
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[["Écarts à traiter", data.total], ["Montants supérieurs", data.superieurs],
          ["Trop perçu", argent(data.trop_percu)], ["Manque encore", argent(data.manque)]]
          .map(([titre, valeur]) => (
            <div key={titre} className="rounded-xl border p-3 bg-white" style={{ borderColor: "var(--border)" }}>
              <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--muted)]">{titre}</div>
              <div className="font-display text-lg mt-0.5">{valeur}</div>
            </div>
          ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {FILTRES.map((f) => (
          <button key={f.id} onClick={() => setFiltre(f.id)} data-testid={`ecarts-filtre-${f.id}`}
            className="px-3 py-1.5 rounded-full text-[11px] font-semibold border transition-transform hover:-translate-y-0.5"
            style={{
              background: filtre === f.id ? "var(--primary)" : "#fff",
              color: filtre === f.id ? "#04202A" : "var(--muted)",
              borderColor: filtre === f.id ? "var(--primary)" : "var(--border)",
            }}>
            {f.label}
            {f.id === "echelonnes" ? ` (${nbEchelonnes})` : ""}
          </button>
        ))}
      </div>

      {relecture && (
        <p className="text-xs" data-testid="ecarts-relecture-resultat" style={{ color: "var(--muted)" }}>
          {relecture}
        </p>
      )}

      <Tableau
        testid="tableau-ecarts"
        entetes={["Famille", "Facture", { label: "Versé", droite: true },
          { label: "Facturé (aujourd'hui)", droite: true },
          { label: "Écart", droite: true }, "Où on en est", ""]}
        lignes={items.map((e, i) => ({ ...e, id: e.cle || `${e.reference}-${i}` }))}
        vide="Aucun écart : chaque versement couvrait exactement sa facture."
        rendu={(e) => (
          <>
            <Cellule fort>
              {e.client || e.payeur || "—"}
              {/* Une ligne par FACTURE : si la famille a versé plusieurs fois, on le dit
                  ici au lieu de répéter la même personne à chaque virement. */}
              <div className="text-xs font-normal text-[var(--muted)]">
                {e.nb_versements > 1
                  ? `${e.nb_versements} versements, du ${jourFr(e.paiements[0]?.date)} au ${jourFr(e.paiements[e.paiements.length - 1]?.date)}`
                  : `versé le ${jourFr(e.date)}`}
                {e.payeur && e.client && e.payeur !== e.client ? ` · par ${e.payeur}` : ""}
                {e.paye_pour ? ` · payé pour ${e.paye_pour}` : ""}
              </div>
            </Cellule>
            <Cellule muet>
              {e.numero || e.reference}
              {e.semestre ? <div className="text-xs">semestre {e.semestre}</div> : null}
            </Cellule>
            <Cellule droite>
              {argent(e.total_verse ?? e.montant)}
              {e.nb_versements > 1 && (
                <div className="text-[11px] text-[var(--muted)]" data-testid="ecart-nb-versements">
                  en {e.nb_versements} fois
                </div>
              )}
            </Cellule>
            <Cellule droite>
              {e.facture_introuvable
                ? <span style={{ color: "#B91C1C" }}>facture introuvable</span>
                : argent(e.total_facture)}
              {e.nb_demandes > 1 && (
                <div className="text-[11px] text-[var(--muted)]">
                  {e.nb_demandes} inscriptions réunies
                </div>
              )}
              {e.total_au_versement > 0 && Math.abs(e.total_au_versement - e.total_facture) > 0.5 && (
                <div className="text-[11px] text-[var(--muted)]">
                  {argent(e.total_au_versement)} au moment du versement
                </div>
              )}
            </Cellule>
            <Cellule droite>
              {e.regle_depuis ? (
                <span className="font-semibold" style={{ color: "#047857" }}>—</span>
              ) : (
                <>
                  <span className="inline-flex items-center gap-1 font-semibold"
                    style={{ color: e.ecart > 0 ? "#1D4ED8" : "#B45309" }}>
                    {e.ecart > 0 ? <ArrowUpRight size={13} /> : <ArrowDownRight size={13} />}
                    {argent(Math.abs(e.ecart))}
                  </span>
                  <div className="text-[11px] text-[var(--muted)]">
                    {e.ecart > 0 ? "versé en trop par la famille" : "manque encore à la famille"}
                  </div>
                </>
              )}
            </Cellule>
            <Cellule>
              {e.regle_depuis
                ? <Badge ton="vert">Compte soldé</Badge>
                : <Badge ton="ambre">Solde {argent(e.solde_actuel)}</Badge>}
              {/* Un versement « trop petit » est souvent une mensualité convenue : on le
                  dit ici, sinon l'école devait ouvrir la facture pour le savoir. */}
              {e.echelonnement_nb > 0 && (
                <div className="mt-1" data-testid="ecart-echelonnement">
                  <Badge ton="violet">
                    <Split size={11} /> Échelonné {e.echelonnement_nb}× ·{" "}
                    {e.echelonnement_recus}/{e.echelonnement_nb} reçu(s)
                  </Badge>
                  <div className="text-[11px] text-[var(--muted)] mt-0.5">
                    {argent(e.echelonnement_part)} par échéance
                    {e.echelonnement_prochaine
                      ? ` · prochaine le ${jourFr(e.echelonnement_prochaine)}`
                      : " · toutes les échéances sont passées"}
                  </div>
                </div>
              )}
              <div className="text-[11px] text-[var(--muted)] mt-1">{e.statut_facture}</div>
              {/* Le détail des versements enregistrés : c'est ici qu'on voit un relevé
                  importé deux fois, la cause la plus fréquente d'un faux « trop payé ». */}
              {(e.paiements || []).length > 0 && (
                <div className="text-[11px] mt-1" data-testid="ecart-paiements">
                  {e.paiements.map((p, k) => (
                    <div key={k} className="text-[var(--muted)]">
                      {jourFr(p.date)} · {argent(p.montant)}{p.payeur ? ` · ${p.payeur}` : ""}
                    </div>
                  ))}
                  {e.doublon_probable && (
                    <div className="mt-1 font-semibold" style={{ color: "#B45309" }} data-testid="ecart-doublon">
                      Deux versements identiques : relevé probablement importé deux fois
                    </div>
                  )}
                </div>
              )}
            </Cellule>
            <Cellule droite>
              <div className="flex flex-col items-end gap-1">
                {onOuvrirFamille && (
                  <button className="btn-ghost" data-testid="ecart-ouvrir"
                    onClick={() => onOuvrirFamille(e)}>Voir la facture</button>
                )}
                {/* Elle a versé moins que prévu : c'est souvent un arrangement accepté par
                    l'école. Autant le poser d'ici, sans aller chercher la facture. */}
                {onEchelonner && !e.regle_depuis && (e.ecart < 0 || e.echelonnement_nb > 0) && (
                  <button className="btn-ghost" data-testid="ecart-echelonner"
                    title={e.echelonnement_nb > 0
                      ? "Modifier ou annuler l'échelonnement convenu avec la famille"
                      : "Poser un paiement en plusieurs fois : aucun rappel ne partira avant la dernière échéance"}
                    onClick={() => onEchelonner(e)}>
                    <Split size={13} /> {e.echelonnement_nb > 0
                      ? "Modifier l'échelonnement" : "Échelonner le paiement"}
                  </button>
                )}
              </div>
            </Cellule>
          </>
        )}
      />

      <p className="text-[11px] text-[var(--muted)]">
        Un écart n'est pas une erreur de calcul : c'est la différence entre <strong>tout ce que
        la famille a versé</strong> et <strong>tout ce qui lui est facturé aujourd'hui pour ce
        semestre</strong>, toutes ses inscriptions réunies. Les causes les plus fréquentes sont un
        virement qui règle plusieurs inscriptions à la fois, un paiement arrondi, un acompte, ou un
        cours ajouté après l'envoi de la facture. Une famille dont le compte est soldé depuis
        <strong> ne figure plus ici</strong> : sa facture est dans « Payées ».
        {data.regles_depuis > 0 && (
          <> {data.regles_depuis} versement(s) signalé(s) à l'époque se sont réglés depuis et sont
          sortis de cette liste.</>
        )}
      </p>
    </div>
  );
}

/* Les paiements partiels SANS accord, à trancher avant tout rappel (école, 20.10.2026) :
   « ceux marqués échelonnés, c'est normal ; les autres, avant de renvoyer des rappels,
   l'école doit les revoir — souvent des paiements échelonnés qu'on a oublié de noter ».
   Trois réponses, chacune avec son effet : paiement en plusieurs fois (l'accord est noté,
   plus de rappel tant qu'il est tenu), prix spécial convenu (la facture se corrige avec le
   rabais), il manque de l'argent (le prochain rappel réclame le reste). Pas de « c'est
   normal » sans rien changer : les montants doivent tomber juste. */
export function AVerifierEcarts({ items, onEchelonner, onPrixSpecial, onIlManque }) {
  if (!items.length) {
    return (
      <p className="text-sm rounded-2xl px-5 py-3" data-testid="ecarts-a-verifier-vide"
        style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", color: "#166534" }}>
        Aucun paiement partiel à vérifier : les rappels peuvent partir sans crainte.
      </p>
    );
  }
  const jours = (iso) => {
    const d = new Date(String(iso || "").slice(0, 10));
    return Number.isNaN(d.getTime()) ? 0 : Math.floor((Date.now() - d.getTime()) / 86400000);
  };
  const bouton = { padding: "6px 10px", fontSize: 12, gap: 5 };
  return (
    <div className="rounded-2xl p-4 space-y-3" data-testid="ecarts-a-verifier"
      style={{ background: "#FFFBEB", border: "1px solid #FDE68A" }}>
      <div>
        <div className="text-sm font-semibold" style={{ color: "#92400E" }}>
          {items.length} paiement(s) partiel(s) à vérifier avant tout rappel
        </div>
        <div className="text-xs mt-0.5" style={{ color: "#92400E" }}>
          Aucun rappel ne part à ces familles tant que tu n'as pas choisi. Souvent, c'est un paiement en
          plusieurs fois qui n'a pas été noté.
        </div>
      </div>
      <Tableau
        testid="tableau-ecarts-a-verifier"
        entetes={["Famille", "Facture", { label: "Reçu", droite: true }, { label: "Reste", droite: true }, "Depuis", ""]}
        lignes={items.map((f) => ({ ...f, id: f.cle }))}
        rendu={(f) => {
          const attente = jours(f.date_paiement || f.envoyee_le);
          return (
            <>
              <Cellule fort>
                {f.client}
                <div className="text-xs font-normal text-[var(--muted)]">
                  {f.genre === "stage" ? f.stage : (f.eleves || []).join(", ")}
                </div>
              </Cellule>
              <Cellule muet>
                n° {f.numero || "—"}{f.genre === "stage" ? " · stage" : f.semestre ? ` · semestre ${f.semestre}` : ""}
                {f.ecart_suggestion_nb > 0 && (
                  <div className="mt-1"><Badge ton="bleu">ressemble à un paiement en {f.ecart_suggestion_nb} fois</Badge></div>
                )}
              </Cellule>
              <Cellule droite>
                <span className="whitespace-nowrap">{argent(f.recu)}</span>
                <div className="text-[11px] text-[var(--muted)] whitespace-nowrap">sur {argent(f.total)}</div>
              </Cellule>
              <Cellule droite fort><span className="whitespace-nowrap" style={{ color: "#B45309" }}>{argent((f.total || 0) - (f.recu || 0))}</span></Cellule>
              <Cellule>
                <span className="text-xs whitespace-nowrap" data-testid="ecart-depuis"
                  style={{ color: attente >= 14 ? "#B91C1C" : "var(--muted)", fontWeight: attente >= 14 ? 600 : 400 }}>
                  {f.date_paiement ? `payé le ${jourFr(f.date_paiement)}` : "—"}
                  {attente >= 14 ? ` · à trancher depuis ${attente} j` : ""}
                </span>
              </Cellule>
              <Cellule largeur={250}>
                <div className="flex flex-col gap-1">
                  <button className="btn-ghost" style={bouton} data-testid="ecart-echelonner" onClick={() => onEchelonner(f)}>
                    <Split size={12} /> Paiement en plusieurs fois
                  </button>
                  <button className="btn-ghost" style={bouton} data-testid="ecart-prix-special" onClick={() => onPrixSpecial(f)}>
                    <Pencil size={12} /> Prix spécial convenu
                  </button>
                  <button className="btn-ghost" style={bouton} data-testid="ecart-il-manque" onClick={() => onIlManque(f)}>
                    <BellRing size={12} /> Il manque de l'argent
                  </button>
                </div>
              </Cellule>
            </>
          );
        }}
      />
    </div>
  );
}
